import React, { useState } from "react";
import { ChefHat, Send, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, fmtPrice } from "@/lib/api";
import { useCart } from "@/context/CartContext";

const EXAMPLES = ["زيت زيتون وزعتر وتمر", "عسل وسمسم", "فريكة ودجاج"];

export default function Cook() {
  const [ingredients, setIngredients] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const { add } = useCart();

  const ask = async (text) => {
    const content = (text ?? ingredients).trim();
    if (!content) return;
    setIngredients(content);
    setBusy(true);
    setResult(null);
    try {
      const r = await api.post("/assistant/cook", { ingredients: content });
      setResult(r.data);
    } catch { toast.error("تعذّر توليد الاقتراح"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen pt-24">
      <div className="relative overflow-hidden bg-olive-deep py-16 text-ivory">
        <div className="tatreez-bg absolute inset-0 opacity-20" />
        <div className="relative mx-auto max-w-3xl px-6 text-center">
          <ChefHat className="mx-auto mb-4 h-10 w-10 text-earth" />
          <h1 className="font-display text-3xl font-bold sm:text-4xl">ماذا أطبخ اليوم؟</h1>
          <p className="mt-3 text-ivory/75">اكتب ما لديك من مكوّنات، ودع «رفيق سهول» يقترح لك وصفات واستخدامات — ويعرض ما ينقصك من المتجر.</p>
          <form onSubmit={(e) => { e.preventDefault(); ask(); }} className="mx-auto mt-7 flex max-w-xl items-center gap-2 rounded-full bg-white p-2">
            <input data-testid="cook-input" value={ingredients} onChange={(e) => setIngredients(e.target.value)}
              placeholder="مثال: زيت زيتون وزعتر وتمر" className="w-full bg-transparent px-4 py-2 text-ink outline-none" />
            <button data-testid="cook-submit" type="submit" disabled={busy} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-olive text-ivory disabled:opacity-60"><Send className="h-5 w-5 -scale-x-100" /></button>
          </form>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            {EXAMPLES.map((e) => (
              <button key={e} onClick={() => ask(e)} className="rounded-full border border-ivory/30 bg-ivory/10 px-3 py-1.5 text-sm text-ivory/90 hover:bg-ivory/20">{e}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-3xl px-6 py-10">
        {busy && <div className="py-16 text-center text-ink/50">يفكّر رفيق سهول...</div>}
        {result && (
          <>
            <div data-testid="cook-reply" className="whitespace-pre-wrap rounded-3xl border border-olive/10 bg-white p-6 leading-relaxed text-ink/80">{result.reply}</div>
            {result.products?.length > 0 && (
              <div className="mt-8">
                <h2 className="mb-4 font-display text-xl font-bold text-ink">منتجات من المتجر تناسبك</h2>
                <div className="space-y-3">
                  {result.products.map((p) => (
                    <div key={p.sku} className="flex items-center gap-4 rounded-2xl border border-olive/10 bg-white p-3">
                      <img src={p.images?.main} alt={p.name_ar} className="h-16 w-16 rounded-xl object-cover" />
                      <div className="flex-1">
                        <div className="font-display font-semibold text-ink">{p.name_ar}</div>
                        <div className="text-sm text-olive">{fmtPrice(p.price)}</div>
                      </div>
                      <button data-testid={`cook-add-${p.sku}`} onClick={() => add(p)} className="flex h-10 items-center gap-1.5 rounded-full bg-olive px-4 text-sm font-medium text-ivory"><Plus className="h-4 w-4" /> أضف</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <p className="mt-6 text-center text-xs text-ink/40">اقتراحات إرشادية غذائية فقط — لا تُعدّ نصيحة طبية.</p>
          </>
        )}
      </div>
    </div>
  );
}
